import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ApiComponentServices } from '../../services/api-services';
import { ActivatedRoute, Router } from '@angular/router';
import { MachineType } from './machineType';
import { GenericService } from '../../generic.service';
import {
  defaultLanguage,
  languages,
} from '../../shared/constants/language.constants';
import { CrowdinService } from '../../shared/service/crowdin.service';
import { devices } from '../../shared/constants/devices.constants';
import { EU_COUNTRY_CODES } from '../../shared/constants/eu-countries.constants';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class LandingService {
  deviceData: any = {};
  accountNumber: string = '';
  selectedMachineType: string | null = null;
  translations: { [key: string]: string } = {};
  currentLanguage: string = defaultLanguage;
  languageOptions: Array<{ value: string }> = [];
  isLoadingTranslations = true;
  public languageName$ = new BehaviorSubject<string>(defaultLanguage);

  constructor(
    private http: HttpClient,
    private apiService: ApiComponentServices,
    private router: Router,
    private route: ActivatedRoute,
    private genericService: GenericService,
    private crowdinService: CrowdinService,
  ) {
    this.route.paramMap.subscribe((params) => {
      this.accountNumber = params.get('accountNumber') || '';
      if (this.accountNumber) {
        const stored = localStorage.getItem('deviceData');
        const deviceData = stored ? JSON.parse(stored) : {};
        deviceData.accountNumber = this.accountNumber;
        localStorage.setItem('deviceData', JSON.stringify(deviceData));
      }
    });
  }

  getQrDetails(qrId: string): Observable<any> {
    const accountNumber = '';
    const accountKey = '';

    return this.apiService.getAccountData(accountNumber, qrId, accountKey);
  }

  getDishMachineDetails(id: number): Observable<any> {
    let url = './assets/data/devices.json';
    return this.http.get<any>(url).pipe(
      // tap(_ => this.log(`fetched hero id=${id}`)),
      catchError(this.handleError<any>(`failed}`)),
    );
  }

  getDishMachineDetailsV2(): Observable<any> {
    let url = './assets/data/devices.json';
    return this.http
      .get<any>(url)
      .pipe(catchError(this.handleError<any>(`failed}`)));
  }

  getDeviceImageByModel(machineType: number): Observable<string> {
    const device = devices.find((d) => d.EnumValue === machineType);
    const imageUrl = device?.Image || '';
    return of(imageUrl ? `./${imageUrl}` : '');
  }

  getDeviceImageByModelFromModelName(model: string): Observable<string> {
    const upperModel = model.toUpperCase();
    const device = devices.find((d) => d.Label === upperModel);
    return of(device?.Image ? `./${device.Image}` : '');
  }

  getMachineTypeMap() {
    const map: {
      [key: number]: { label: string; image: string; type: string };
    } = {};
    devices.forEach((device) => {
      map[device.EnumValue] = {
        label: device.Label,
        image: device.Icon,
        type: device.Type,
      };
    });
    return map;
  }

  getMachineTypeImage() {
    const map: { [key: number]: { label: string; image: string } } = {};
    devices.forEach((device) => {
      map[device.EnumValue] = {
        label: device.Label,
        image: device.Image,
      };
    });
    return map;
  }

  public machineName(name: string): string {
    const upperName = name.toUpperCase();
    if (upperName.includes('ES2000')) {
      return 'ES2000';
    }
    if (upperName.includes('EH500S')) {
      return 'EH500S';
    }
    return name.replace(/[^a-zA-Z]/g, '').toUpperCase();
  }

  navigateToLandingPage(router: Router, deviceData: any): void {
    const accountNumber = deviceData?.accountNumber || '';
    router.navigate([`troubleshoot/${accountNumber}`]);
  }

  moveToNextImage(popupDetails: any, popupImageIndex: number): number {
    const images = popupDetails.images || [];
    if (!images.length) return popupImageIndex;

    if (popupImageIndex <= 1) {
      popupImageIndex = 2;
    } else {
      if (popupImageIndex < images.length - 1) {
        popupImageIndex++;
      }
    }
    return popupImageIndex;
  }

  moveToPreviousImage(popupDetails: any, popupImageIndex: number): number {
    const images = popupDetails.images || [];
    if (!images.length) return popupImageIndex;

    if (popupImageIndex > 2) {
      popupImageIndex--;
    } else if (popupImageIndex === 2) {
      popupImageIndex = 0;
    } else {
      popupImageIndex = 0;
    }
    return popupImageIndex;
  }

  updateDotIndicator(popupDetails: any, popupImageIndex: number): number {
    if (!popupDetails.images || popupDetails.images.length === 0) {
      return 0;
    }

    if (popupImageIndex === 0 || popupImageIndex === 1) {
      return 0;
    } else {
      return popupImageIndex >= 2 && popupImageIndex <= 4
        ? popupImageIndex - 1
        : 0;
    }
  }

  navigateToHomePage() {
    const storedData = localStorage.getItem('deviceData');
    if (storedData) {
      this.deviceData = JSON.parse(storedData);
    }
    this.selectedMachineType = this.deviceData.selectedMachineType;
    this.genericService.deviceData.next(this.deviceData);
    const accountNumber = this.deviceData?.accountNumber || this.accountNumber;

    if (
      this.deviceData?.routeFromMobile &&
      this.deviceData.routeFromMobile.trim() === 'issue-list'
    ) {
      this.router.navigate([`issue-list/${accountNumber}`], {
        queryParams: {
          machineType: this.deviceData.selectedMachineType,
          accountName: this.deviceData.accountName,
          accountKey: this.deviceData.accountKey,
          state: this.deviceData.state,
          postalCode: this.deviceData.zip,
          countryCode: this.deviceData.countryCode,
        },
      });
    } else {
      this.router.navigate([`troubleshoot/${accountNumber}`]);
    }
  }

  needHelp() {
    const storedData = localStorage.getItem('deviceData');
    if (storedData) {
      this.deviceData = JSON.parse(storedData);
    }
    const accountNumber = this.deviceData?.accountNumber || this.accountNumber;
    this.router.navigate([`/esrSupport/${accountNumber}`]);
  }

  setSelectedDevice(paramName: string): MachineType | null {
    const currentMachineType = this.route.snapshot.queryParamMap.get(paramName);
    if (!currentMachineType) return null;

    const machineTypeUpper = currentMachineType.toUpperCase();

    if (machineTypeUpper.includes('ES2000')) {
      return MachineType.ES2000;
    }
    if (machineTypeUpper.includes('EHT')) {
      return MachineType.EHT;
    }
    if (machineTypeUpper.includes('ELT')) {
      return MachineType.ELT;
    }
    if (machineTypeUpper.includes('XLHT')) {
      return MachineType.XLHT;
    }
    if (machineTypeUpper.includes('ULT')) {
      return MachineType.EULT;
    }
    if (machineTypeUpper.includes('UHT')) {
      return MachineType.EUHT;
    }
    if (machineTypeUpper.includes('EH500S')) {
      return MachineType.EH500S;
    }
    if (machineTypeUpper.includes('XLRW')) {
      return MachineType.XLRW;
    }

    for (const key in MachineType) {
      if (MachineType[key as keyof typeof MachineType] === currentMachineType) {
        return MachineType[key as keyof typeof MachineType];
      }
    }

    return null;
  }

  getAccountKey(): string | null {
    return this.route.snapshot.queryParamMap.get('accountKey');
  }

  getAccountName(): string | null {
    return this.route.snapshot.queryParamMap.get('accountName');
  }

  getLanguageOptions = () => {
    return languages.map((lang) => ({ value: lang.value }));
  };

  async languageChange(event: any) {
    const selectedLang = event.target.value;
    await this.loadTranslations(selectedLang);
  }

  async loadTranslations(languageInput: string) {
    this.isLoadingTranslations = true;
    const langDef =
      languages.find(
        (l) =>
          l.value.toLowerCase() === languageInput.toLowerCase() ||
          l.code.toLowerCase() === languageInput.toLowerCase(),
      ) || languages[0];

    const langCode = langDef.code;
    const languageName = langDef.value;

    await this.crowdinService.changeLanguage(langCode);
    this.translations = this.crowdinService.getAllTranslations();

    this.currentLanguage = languageName;
    this.languageName$.next(languageName);

    try {
      const stored = localStorage.getItem('deviceData');
      const deviceData = stored ? JSON.parse(stored) : {};
      deviceData.languageSelected = languageName;
      localStorage.setItem('deviceData', JSON.stringify(deviceData));
      this.genericService.deviceData.next(deviceData);
    } catch (e) {
      console.error('Failed to persist languageSelected:', e);
    }

    this.isLoadingTranslations = false;
  }

  getLanguageCode(languageInput: string): string {
    return (
      languages.find(
        (l) =>
          l.value.toLowerCase() === languageInput.toLowerCase() ||
          l.code.toLowerCase() === languageInput.toLowerCase(),
      )?.code || 'en'
    );
  }

  /**
   * Checks if a country code is in the EU
   * @param countryCode - ISO 3166-1 alpha-3 country code
   * @returns true if the country is in the EU
   */
  isEuCountry(countryCode: string): boolean {
    const normalizedCode = (countryCode || '').trim().toUpperCase();
    return !!EU_COUNTRY_CODES[normalizedCode];
  }

  /**
   * Initializes custom chat fields for CXOne chat support
   * Configures chat based on EU/non-EU location and sets custom fields
   * @param customData - Object containing account number, name, machine model, state, and zip code
   * @param countryCode - ISO 3166-1 alpha-3 country code (optional, for EU detection)
   */
  initializeChatCustomFields(
    customData: {
      accountNumber: string;
      accountName: string;
      machineModel: string;
      state: string;
      zip: string;
    },
    countryCode?: string,
  ): void {
    const customProperty = (window as any).cxone;

    if (typeof customProperty === 'undefined') {
      return;
    }

    const isEu = countryCode ? this.isEuCountry(countryCode) : false;

    // Initialize with appropriate template ID based on region
    customProperty('init', isEu ? '1351' : '1488');
    customProperty(
      'guide',
      'init',
      isEu ? '48fcdd03-6749-4c04-a736-72e673ceb9e8' : environment.liveChatId,
    );

    // Set custom fields for the chat case
    customProperty(
      'chat',
      'setCaseCustomField',
      'acctnumber',
      customData.accountNumber,
    );
    customProperty(
      'chat',
      'setCaseCustomField',
      'account_name',
      customData.accountName,
    );
    customProperty(
      'chat',
      'setCaseCustomField',
      'machine_model',
      customData.machineModel,
    );
    customProperty('chat', 'setCaseCustomField', 'state', customData.state);
    customProperty('chat', 'setCaseCustomField', 'zip_code', customData.zip);
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   *
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      // TODO: send the error to remote logging infrastructure
      console.error(error); // log to console instead

      // TODO: better job of transforming error for user consumption
      this.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }

  /** Log a HeroService message with the MessageService */
  private log(message: string) {}
}
